import { z } from 'zod/v4';
import { matchingResolutionEnum } from '~/schemas/enums';

export const aggregateFunctions = z.enum(['sum', 'avg', 'min', 'max', 'mean']);
export const filterTypes = z.enum([
  'between',
  'one_of',
  'less_than_or_equal_to',
  'greater_than_or_equal_to',
  'greater_than',
  'less_than',
  'on_or_after',
  'before',
]);
export type FilterTypes = z.infer<typeof filterTypes>;

export const sortingOrders = z.enum(['ascending', 'descending']);
export type SortingOrder = z.infer<typeof sortingOrders>;

export const metricsControlKeysEnum = z.enum([
  'ot_plotting_resolution_auto',
  'ot_zoom_start_datetime',
  'ot_zoom_end_datetime',
  'ot_plotting_resolution',
  'matching_average_plotting_resolution',
  'breakdown_by',
]);

export type MetricsControlKeys = z.infer<typeof metricsControlKeysEnum>;

export const datetimeBinEnum = z.enum([
  'five_min_of_hour',
  'fifteen_min_of_hour',
  'thirty_min_of_hour',
  'hour',
  'day',
  'day_of_week',
  'month',
  'quarter',
  'year',
  'year_from_april',
  'year_from_may',
]);

export type DateTimeBin = z.infer<typeof datetimeBinEnum>;

// utc_offset is not a period: it is the UTC offset in minutes of the bin start. It tells apart the two bins that
// share a local time when the clocks go back.
export const periodStartBinEnum = z.enum([...datetimeBinEnum.options, 'utc_offset']);
export type PeriodStartBin = z.infer<typeof periodStartBinEnum>;

export const plottingResolutionEnum = z.enum([...datetimeBinEnum.options, 'whole_period']);
export type PlottingResolution = z.infer<typeof plottingResolutionEnum>;

// Filter schemas
export const periodStartFilter = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime(), // Inclusive
  type: filterTypes, // Exclusive
});

export const stringValueFilter = z.object({
  type: filterTypes,
  value: z.string(),
});

export const stringArrayFilter = z.object({
  items: z.array(z.string().nullable()),
  type: filterTypes,
});

export const nestedStringArrayFilter = z.object({
  items: z.array(z.array(z.string()).nullable()),
  type: filterTypes,
});

export type StringValueFilter = z.infer<typeof stringValueFilter>;
export type StringArrayFilter = z.infer<typeof stringArrayFilter>;
export type NestedStringArrayFilter = z.infer<typeof nestedStringArrayFilter>;

// Pagination schema
export const pagination = z.object({
  limit: z.number().int().positive().optional(),
  skip: z.number().int().nonnegative().optional(),
});

// Create request and response schema functions
export function createMetricsTableRequest<
  T extends z.ZodUnion<readonly [z.ZodTypeAny, ...z.ZodTypeAny[]]>,
  U extends z.ZodUnion<readonly [z.ZodTypeAny, ...z.ZodTypeAny[]]>,
>(columnsUnion: T, labelUnion: U) {
  return z.object({
    views: z.array(
      z.object({
        groups: z
          .array(
            z.object({
              bin: periodStartBinEnum.optional(),
              column: columnsUnion,
              limit: z.number().optional(),
            }),
          )
          .optional(),
        label: labelUnion,
        matching_resolution: matchingResolutionEnum.optional(),
        metrics: z.array(
          z.object({
            aggregation_function: aggregateFunctions,
            column: columnsUnion,
          }),
        ),
        pagination: pagination.optional(),
        paging: z
          .object({
            from: z.number(),
            to: z.number(),
          })
          .optional(),
        sort: z
          .array(
            z.object({
              bin: periodStartBinEnum.optional(),
              column: columnsUnion,
              order: sortingOrders,
            }),
          )
          .optional(),
      }),
    ),
  });
}

export function createMatchingAndPlottingResolutionGroups<
  T extends z.ZodUnion<readonly [z.ZodTypeAny, ...z.ZodTypeAny[]]>,
>(columnsUnion: T) {
  return z.array(columnsUnion).optional();
}

// Response data schema
const scalarData = z.union([z.string(), z.number(), z.null()]);
const cellData = z.union([scalarData, z.array(scalarData)]);
const rowData = z.array(cellData);
export type MetricsRowData = z.infer<typeof rowData>;

export function createMetricsTableResponse<
  T extends z.ZodUnion<readonly [z.ZodTypeAny, ...z.ZodTypeAny[]]>,
  U extends z.ZodUnion<readonly [z.ZodTypeAny, ...z.ZodTypeAny[]]>,
>(columnsUnion: T, labelUnion: U) {
  return z.object({
    views: z.array(
      z.object({
        columns: z.array(z.object({ bin: periodStartBinEnum.optional(), column: columnsUnion })).optional(),
        data: z.array(rowData).optional(),
        errors: z
          .array(
            z.object({
              code: z.string(),
              description: z.string(),
            }),
          )
          .optional(),
        label: labelUnion,
      }),
    ),
  });
}

/* -------------------------------------------------------------------------------------------------
 * Metrics filter values
 * -----------------------------------------------------------------------------------------------*/

const stringFilterValues = z.array(z.string().nullable());
const arrayFilterValues = z.array(z.array(z.string()).nullable());

const productionDeviceFilterValues = z.array(
  z.object({ ext_id: z.string().nullable(), id: z.uuid(), name: z.string(), reference: z.string() }).nullable(),
);

const supplyProductionDeviceFilterValues = productionDeviceFilterValues.transform((data) => {
  return data.map((item) => {
    if (!item) {
      return null;
    }

    const { id, name, reference, ...rest } = item;
    return {
      ...rest,
      production_device_client_reference: reference,
      production_device_id: id,
      production_device_name: name,
    };
  });
});

// One list of devices serves both portfolio sides, as the other attribute filter values do. A sell deal
// tranche can only name a device of the organisation, so the demand side needs no list of its own.
const prefixProductionDeviceFilterValues = (
  data: z.infer<typeof productionDeviceFilterValues>,
  prefix: 'supply' | 'demand',
) =>
  data.map((item) => {
    if (!item) {
      return null;
    }

    const { id, name, reference, ext_id } = item;
    return {
      [`${prefix}_production_device_client_reference`]: reference,
      [`${prefix}_production_device_id`]: id,
      [`${prefix}_production_device_name`]: name,
      [`${prefix}_production_device_registry_identifier`]: ext_id,
    };
  });

const demandProductionDeviceFilterValues = productionDeviceFilterValues.transform((data) => {
  return data.map((item) => {
    if (!item) {
      return null;
    }

    const { id, name, reference, ext_id } = item;
    return {
      production_device_client_reference: reference,
      production_device_id: id,
      production_device_name: name,
      production_device_registry_identifier: ext_id,
    };
  });
});

const dealsFilterValues = z.array(z.object({ id: z.uuid(), reference: z.string() }).nullable()).transform((data) => {
  return data.map((item) => {
    if (!item) {
      return null;
    }

    const { id, reference, ...rest } = item;
    return {
      ...rest,
      deal_client_reference: reference,
      deal_id: id,
    };
  });
});

const counterpartiesFilterValues = z
  .array(z.object({ id: z.uuid(), name: z.string(), reference: z.string() }).nullable())
  .transform((data) => {
    return data.map((item) => {
      if (!item) {
        return null;
      }

      const { id, name, reference, ...rest } = item;
      return {
        ...rest,
        counterparty_client_reference: reference,
        counterparty_id: id,
        counterparty_name: name,
      };
    });
  });

const supplyAndDemandFilterValues = z.object({
  certificate_type: stringFilterValues,
  counterparty: counterpartiesFilterValues,
  country: arrayFilterValues,
  deal: dealsFilterValues,
  deal_tranche_volume_type: stringFilterValues,
  sub_national_region: arrayFilterValues,
  subsidy_regime: arrayFilterValues,
  supra_national_region: arrayFilterValues,
  technology: arrayFilterValues,
});

export const supplyFilterValuesResponse = supplyAndDemandFilterValues
  .extend({
    production_device: supplyProductionDeviceFilterValues,
    production_device_client_reference: stringFilterValues,
    production_device_name: stringFilterValues,
  })
  .transform((data) => {
    const { country, production_device, deal, counterparty, ...rest } = data;
    return {
      ...rest,
      counterparty_id: counterparty,
      deal_id: deal,
      generation_country: country,
      production_device_id: production_device,
    };
  });

export const demandFilterValuesResponse = supplyAndDemandFilterValues
  .extend({
    counterparty_country: stringFilterValues,
    production_device: demandProductionDeviceFilterValues,
  })
  .transform((data) => {
    const { deal, counterparty, production_device, ...rest } = data;
    return {
      ...rest,
      counterparty_id: counterparty,
      deal_id: deal,
      production_device_id: production_device,
    };
  });

export const portfolioFilterValuesResponse = z
  .object({
    buy_deal: dealsFilterValues,
    certificate_type: stringFilterValues,
    counterparty: counterpartiesFilterValues,
    counterparty_country: stringFilterValues,
    country: arrayFilterValues,
    deal_tranche_volume_type: stringFilterValues,
    production_device: productionDeviceFilterValues,
    sell_deal: dealsFilterValues,
    subsidy_regime: arrayFilterValues,
    technology: arrayFilterValues,
  })
  .transform((data) => {
    const { buy_deal, counterparty, deal_tranche_volume_type, production_device, sell_deal, ...rest } = data;
    return {
      ...rest,
      buy_deal_id: buy_deal,
      counterparty_id: counterparty,
      demand_sell_deal_tranche_volume_type: deal_tranche_volume_type,
      demand_production_device_id: prefixProductionDeviceFilterValues(production_device, 'demand'),
      sell_deal_id: sell_deal,
      supply_buy_deal_tranche_volume_type: deal_tranche_volume_type,
      supply_production_device_id: prefixProductionDeviceFilterValues(production_device, 'supply'),
    };
  });

export type DemandFilterValuesResponse = z.infer<typeof demandFilterValuesResponse>;
export type SupplyFilterValuesResponse = z.infer<typeof supplyFilterValuesResponse>;
export type PortfolioFilterValuesResponse = z.infer<typeof portfolioFilterValuesResponse>;

// Plotting resolution response
export const plottingResolutionsResponseSchema = z.object({
  plotting_resolutions: z.array(plottingResolutionEnum),
});

export type PlottingResolutionsResponse = z.infer<typeof plottingResolutionsResponseSchema>;
